import { useQuery } from '@tanstack/react-query';
import { DollarSign } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getUsageSummary } from '../../api/assistant';

function formatCost(amount: number): string {
  if (amount === 0) return '$0.00';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}

function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(2)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

/**
 * Shows the admin's running estimated AI assistant spend so it doesn't look like a free,
 * unmetered feature. Cost is an ESTIMATE based on configured per-model pricing (DB-backed —
 * see backend LlmModelPriceService) — it may not exactly match the actual AWS bill, especially
 * for models with no configured price, which are called out explicitly here.
 */
export function UsageBadge() {
  const { data: usage } = useQuery({
    queryKey: ['assistant-usage'],
    queryFn: getUsageSummary,
    staleTime: 10_000,
  });

  if (!usage) return null;

  const hasUsage = usage.totalInputTokens > 0 || usage.totalOutputTokens > 0;
  const hasCap = usage.spendLimitEnabled && usage.dailyCapUsd != null && usage.spentTodayUsd != null;
  if (!hasUsage && !hasCap) return null;

  const capPct = hasCap ? Math.min(100, (usage.spentTodayUsd! / usage.dailyCapUsd!) * 100) : 0;
  const nearOrOverCap = hasCap && capPct >= 80;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors hover:bg-muted ${
            nearOrOverCap ? 'border-amber-500/40 text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
          }`}
          title="Estimated AI assistant spend"
        >
          <DollarSign className="h-3 w-3" />
          <span>{formatCost(usage.estimatedCostUsd)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 text-sm">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold">Estimated AI spend</span>
            <span className="font-semibold">{formatCost(usage.estimatedCostUsd)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatTokens(usage.totalInputTokens)} input / {formatTokens(usage.totalOutputTokens)} output tokens across all your conversations.
          </p>

          {hasCap && (
            <div className="border-t pt-2 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Today's usage</span>
                <span className={nearOrOverCap ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}>
                  {formatCost(usage.spentTodayUsd!)} / {formatCost(usage.dailyCapUsd!)}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full rounded-full ${nearOrOverCap ? 'bg-amber-500' : 'bg-primary'}`}
                  style={{ width: `${capPct}%` }}
                />
              </div>
            </div>
          )}

          {usage.byModel.length > 0 && (
            <div className="border-t pt-2 space-y-1.5">
              {usage.byModel.map((m) => (
                <div key={m.modelId} className="flex items-center justify-between text-xs">
                  <span className="truncate pr-2" title={m.modelId}>
                    {m.modelId.split('.').pop()?.replace(/-v\d+:\d+$/, '') ?? m.modelId}
                  </span>
                  <span className={m.priced ? '' : 'text-muted-foreground italic'}>
                    {m.priced
                      ? formatCost(m.estimatedCostUsd)
                      : `${formatTokens(m.inputTokens + m.outputTokens)} tokens (unpriced)`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {usage.hasUnpricedUsage && (
            <p className="text-[11px] text-muted-foreground border-t pt-2">
              Some usage is from a model with no configured price and isn't included in the total above.
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Estimate only, based on configured per-model rates, may not exactly match your AWS bill.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
